import React, { startTransition } from 'react';
import { useNavigate } from 'react-router-dom';
import './ErrorBoundary.css';

interface ErrorFallbackProps {
  error: Error | null;
  onReset: () => void;
}

/**
 * Fallback UI displayed when an error boundary catches an error.
 * Provides options to retry or navigate to a safe page.
 */
const ErrorFallback: React.FC<ErrorFallbackProps> = ({ error, onReset }) => {
  const navigate = useNavigate();

  const handleGoToReports = () => {
    // React Router renders navigations as transitions. A plain onReset() would
    // render first, on its own, and re-run the page that just crashed. Inside
    // the same transition, the reset and the new route render together.
    startTransition(() => {
      onReset();
      navigate('/reports');
    });
  };

  const handleTryAgain = () => {
    onReset();
    window.location.reload();
  };

  return (
    <div className="error-boundary-container">
      <div className="error-boundary-card">
        <div className="error-boundary-icon">
          <i className="bi bi-exclamation-triangle-fill"></i>
        </div>
        <h2 className="error-boundary-title">Something went wrong</h2>
        <p className="error-boundary-message">
          We encountered an unexpected error. Please try again or return to the reports page.
        </p>
        {error && process.env.NODE_ENV === 'development' && (
          <details className="error-boundary-details">
            <summary>Error Details</summary>
            <pre>{error.message}</pre>
          </details>
        )}
        <div className="error-boundary-actions">
          <button
            className="btn btn-primary"
            onClick={handleTryAgain}
          >
            <i className="bi bi-arrow-clockwise me-2"></i>
            Try Again
          </button>
          <button
            className="btn btn-outline-primary"
            onClick={handleGoToReports}
          >
            <i className="bi bi-file-earmark-text me-2"></i>
            Go to Reports
          </button>
        </div>
      </div>
    </div>
  );
};

export default ErrorFallback;
